import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { useExportGoalToExcel } from '@/shared/hooks/useIssues';
import type { IssueWithChildren } from '@momentum/types';
import { hasGoalGoalsTasksHierarchy } from '../../utils/goal-hierarchy.utils';

interface ExportToExcelButtonProps {
  goal: IssueWithChildren;
}

export const ExportToExcelButton: React.FC<ExportToExcelButtonProps> = ({
  goal,
}) => {
  const { t } = useTranslation('goals');
  const exportMutation = useExportGoalToExcel();

  // Check if goal has the correct hierarchy
  const canExport = hasGoalGoalsTasksHierarchy(goal);

  if (!canExport) {
    return null;
  }

  const handleExport = async () => {
    try {
      // Get column headers from translations
      const columnHeaders = {
        idx: t('excelColumnIdx'),
        title: t('excelColumnTitle'),
        owner: t('excelColumnOwner'),
        assignees: t('excelColumnAssignees'),
        startTime: t('excelColumnStartTime'),
        endTime: t('excelColumnEndTime'),
        duration: t('excelColumnDuration'),
        actualEndDate: t('excelColumnActualEndDate'),
        remarks: t('excelColumnRemarks'),
      };

      // Get header labels from translations
      const headerLabels = {
        creator: t('excelHeaderCreator'),
        exportDate: t('excelHeaderExportDate'),
        goalDueDate: t('excelHeaderGoalDueDate'),
      };

      const blob = await exportMutation.mutateAsync({
        goalId: goal._id,
        data: { columnHeaders, headerLabels },
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleExport}
      disabled={exportMutation.isPending}
      className="gap-2"
    >
      {exportMutation.isPending ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('exporting')}
        </>
      ) : (
        <>
          <Download className="h-4 w-4" />
          {t('exportToExcel')}
        </>
      )}
    </Button>
  );
};
